import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  IconLoader2, IconArrowRight, IconUsersGroup, IconPlus, IconTrash,
  IconAlertTriangle, IconShieldCheck, IconCircleCheck,
} from "@tabler/icons-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";

export default function FamilySetup() {
  const { txId } = useParams();
  const nav = useNavigate();
  const { lang } = useI18n();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null);
  const [error, setError] = useState("");

  const [child, setChild] = useState({ name: "", age: 8, grade: "", condition: "" });
  const [families, setFamilies] = useState([{ name: "", email: "" }]);
  const [teacher, setTeacher] = useState({ name: "", email: "" });
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/family/spaces/pending/${txId}`);
        if (!data.pending && data.child_id) {
          // Already configured — jump to Círculo
          nav(`/app/circulo/${data.child_id}`);
          return;
        }
        setPending(data);
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [txId, nav]);

  const addFamily = () => {
    if (families.length >= 2) return;
    setFamilies([...families, { name: "", email: "" }]);
  };
  const removeFamily = (i) => {
    setFamilies(families.filter((_, idx) => idx !== i));
  };
  const setFamily = (i, k, v) => {
    const next = [...families];
    next[i] = { ...next[i], [k]: v };
    setFamilies(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      const family_emails = families.filter(f => f.email).map(f => f.email.trim());
      const family_names = families.filter(f => f.email).map(f => f.name.trim() || f.email.split("@")[0]);
      const body = {
        child_name: child.name.trim(),
        child_age: Number(child.age),
        child_grade: child.grade.trim() || null,
        child_condition: child.condition.trim() || null,
        family_emails,
        family_names,
        teacher_email: teacher.email.trim() || null,
        teacher_name: teacher.name.trim() || null,
        consent: consent,
      };
      const { data } = await api.post(`/family/spaces/setup/${txId}`, body);
      setResult(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="wrap-app py-10 text-tert font-mono text-[11px] uppercase">
        <IconLoader2 size={14} className="animate-spin inline mr-2" /> Cargando…
      </div>
    );
  }

  if (error && !pending) {
    return (
      <div className="wrap-app py-10">
        <p className="text-errtext text-[14px]" data-testid="family-setup-error">{error}</p>
        <button className="btn mt-4" onClick={() => nav("/app/billing")}>Volver</button>
      </div>
    );
  }

  if (result) {
    return (
      <div className="wrap-app max-w-[720px] py-10" data-testid="family-setup-success">
        <div className="card border-l-[4px] border-l-oktext bg-okbg">
          <div className="flex items-start gap-3">
            <IconCircleCheck size={40} className="text-oktext shrink-0" />
            <div className="flex-1">
              <p className="lbl m-0 text-oktext">{lang === "es" ? "Espacio familiar creado" : "Family space created"}</p>
              <h1 className="font-display text-[24px] font-medium mt-1">
                {lang === "es" ? `Bienvenidos al Círculo de ${child.name}` : `Welcome to ${child.name}'s Círculo`}
              </h1>
              <ul className="mt-4 space-y-1 text-[13.5px] text-muted">
                <li>· {result.parents_added} {lang === "es" ? "familiares con acceso (incluido tú)" : "family members with access (including you)"}</li>
                <li>· {result.invites_created.length} {lang === "es" ? "docente(s) invitado(s)" : "teacher(s) invited"}</li>
                {result.admin_auto_added && (
                  <li className="text-teal-deep">· {lang === "es" ? "Dra. Mariela Chiessa agregada automáticamente" : "Dra. Mariela Chiessa added automatically"}</li>
                )}
              </ul>
              <div className="flex gap-2 mt-6">
                <button className="btn" onClick={() => nav(`/app/circulo/${result.child_id}`)} data-testid="family-goto-space">
                  {lang === "es" ? "Entrar al espacio" : "Enter the space"} <IconArrowRight size={13} />
                </button>
                <button className="btn-ghost btn" onClick={() => nav("/app/billing")}>
                  {lang === "es" ? "Ver mi historial" : "See history"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wrap-app max-w-[760px] py-8" data-testid="family-setup-page">
      <div className="card border-l-[4px] border-l-teal bg-teal-soft">
        <div className="flex items-start gap-3">
          <IconCircleCheck size={28} className="text-teal-deep shrink-0" />
          <div>
            <p className="lbl m-0 text-teal-deep">{lang === "es" ? "Pago confirmado" : "Payment confirmed"}</p>
            <p className="text-[13.5px] mt-1">
              {lang === "es"
                ? `Gracias, ${user?.name || user?.email}. Configura ahora el espacio familiar para acceder a Círculo.`
                : `Thanks ${user?.name || user?.email}. Now set up the family space to access Círculo.`}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="mt-5 space-y-6">
        {/* CHILD */}
        <section className="card">
          <p className="lbl m-0">{lang === "es" ? "Sobre el niño o niña" : "About the child"}</p>
          <p className="text-tert text-[11.5px] mt-0.5">{lang === "es" ? "Solo tú, la familia y quienes invites verán esta información." : "Only you, the family and invitees will see this."}</p>
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <div className="field mb-0">
              <label>{lang === "es" ? "Nombre" : "Name"} *</label>
              <input className="input" required value={child.name} onChange={(e) => setChild({ ...child, name: e.target.value })} data-testid="child-name" placeholder="Ej: Sofía" />
            </div>
            <div className="field mb-0">
              <label>{lang === "es" ? "Edad" : "Age"} *</label>
              <input className="input" type="number" min="1" max="25" required value={child.age} onChange={(e) => setChild({ ...child, age: e.target.value })} data-testid="child-age" />
            </div>
            <div className="field mb-0">
              <label>{lang === "es" ? "Grado / Nivel" : "Grade / Level"}</label>
              <input className="input" value={child.grade} onChange={(e) => setChild({ ...child, grade: e.target.value })} data-testid="child-grade" placeholder={lang === "es" ? "Ej: 2do grado" : "e.g. Grade 2"} />
            </div>
            <div className="field mb-0">
              <label>{lang === "es" ? "Condición o diagnóstico (opcional)" : "Condition or diagnosis (optional)"}</label>
              <input className="input" value={child.condition} onChange={(e) => setChild({ ...child, condition: e.target.value })} data-testid="child-condition" placeholder={lang === "es" ? "TEA, TDAH, ..." : "ASD, ADHD, ..."} />
            </div>
          </div>
        </section>

        {/* FAMILY INVITES */}
        <section className="card">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="lbl m-0 flex items-center gap-2"><IconUsersGroup size={13} /> {lang === "es" ? "Familia" : "Family"}</p>
              <p className="text-tert text-[11.5px] mt-0.5">
                {lang === "es" ? "Invita hasta 2 personas más (madre, padre, tutor). Tú ya estás incluido." : "Invite up to 2 more people (mother, father, guardian). You are already included."}
              </p>
            </div>
            {families.length < 2 && (
              <button type="button" onClick={addFamily} className="btn-ghost btn text-[12px]" data-testid="add-family">
                <IconPlus size={12} /> {lang === "es" ? "Agregar" : "Add"}
              </button>
            )}
          </div>
          <div className="space-y-2 mt-4">
            {families.map((f, i) => (
              <div key={i} className="grid sm:grid-cols-[1fr_1.4fr_auto] gap-2" data-testid={`family-row-${i}`}>
                <input className="input" placeholder={lang === "es" ? "Nombre" : "Name"} value={f.name} onChange={(e) => setFamily(i, "name", e.target.value)} data-testid={`family-name-${i}`} />
                <input className="input" type="email" placeholder="correo@ejemplo.com" value={f.email} onChange={(e) => setFamily(i, "email", e.target.value)} data-testid={`family-email-${i}`} />
                <button type="button" className="btn-ghost btn text-[12px] px-2" onClick={() => removeFamily(i)} aria-label="Quitar">
                  <IconTrash size={12} />
                </button>
              </div>
            ))}
            {families.length === 0 && (
              <p className="text-tert text-[12px]">{lang === "es" ? "Sin invitados de familia. Puedes agregarlos después." : "No family invitees. You can add them later."}</p>
            )}
          </div>
        </section>

        {/* TEACHER */}
        <section className="card">
          <p className="lbl m-0">{lang === "es" ? "Docente" : "Teacher"}</p>
          <p className="text-tert text-[11.5px] mt-0.5">
            {lang === "es" ? "El docente principal del aula del niño (opcional). Tendrá acceso a Círculo." : "Child's main classroom teacher (optional). Will have access to Círculo."}
          </p>
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <input className="input" placeholder={lang === "es" ? "Nombre del docente" : "Teacher name"} value={teacher.name} onChange={(e) => setTeacher({ ...teacher, name: e.target.value })} data-testid="teacher-name" />
            <input className="input" type="email" placeholder="docente@escuela.com" value={teacher.email} onChange={(e) => setTeacher({ ...teacher, email: e.target.value })} data-testid="teacher-email" />
          </div>
        </section>

        {/* CONSENT */}
        <section className="card border-l-[4px] border-l-honey">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" data-testid="consent-checkbox" required />
            <div>
              <p className="font-medium text-[13.5px] flex items-center gap-2">
                <IconShieldCheck size={14} className="text-teal-deep" />
                {lang === "es" ? "Consentimiento" : "Consent"}
              </p>
              <p className="text-tert text-[12px] mt-1">
                {lang === "es"
                  ? "Confirmo que los correos que ingreso pertenecen a personas mayores de edad o tutores legales, y que han aceptado recibir invitación y compartir esta información dentro del espacio de Círculo."
                  : "I confirm that the emails belong to adults or legal guardians who have consented to receive an invitation and share this information inside Círculo."}
              </p>
            </div>
          </label>
        </section>

        {error && (
          <div className="text-errtext text-[13px] flex items-center gap-2" data-testid="family-setup-error">
            <IconAlertTriangle size={14} /> {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button type="submit" className="btn" disabled={saving || !consent || !child.name} data-testid="family-setup-submit">
            {saving ? <IconLoader2 size={13} className="animate-spin" /> : <IconCircleCheck size={13} />}
            {lang === "es" ? "Crear espacio familiar" : "Create family space"}
          </button>
        </div>
      </form>
    </div>
  );
}
